import styled from "@emotion/styled";

import { DesignToken, SpacingToken, Text } from "@livechat/design-system-react-components";

import { AccordionItem } from "components/accordion-item/AccordionItem";
import { StyledLink } from "components/links/StyledLink";
import { getIsMemberOfTicketTeam } from "helpers/tickets";
import { ITicket } from "interfaces/entities/ticket";
import { SettingsRoutes } from "routes/settings/constants";
import { IAgent } from "store/entities/agents";
import { ICustomField } from "store/entities/custom-fields/interfaces";
import { withVar } from "themes/helpers";

import { TicketInfoCustomField } from "./TicketInfoCustomField";
import { SectionWrapper } from "./styles";

interface IProps {
  ticket: ITicket;
  currentAgent: IAgent;
  customFields: ICustomField[];
  opened: boolean;
  onHeaderClick(): void;
}

export const TicketDetailsCustomFields: React.FC<IProps> = ({
  ticket,
  currentAgent,
  customFields,
  opened,
  onHeaderClick,
}) => {
  const isMemberOfTicketTeam = getIsMemberOfTicketTeam(currentAgent, ticket);
  const availableCustomFields = customFields.filter(
    customField => customField.teamIDs.some(id => ticket.teamIds.includes(id)) && customField.status !== "deactivated"
  );

  return (
    <SectionWrapper borderTop>
      <AccordionItem title="Custom fields" opened={opened} onHeaderClick={onHeaderClick}>
        {availableCustomFields.length === 0 && (
          <EmptyStateContainer>
            <Text bold>No custom fields yet</Text>
            <Text size="sm" customColor={withVar(DesignToken.ContentBasicSecondary)} textAlign="center">
              Create your first custom field to add more useful details to your tickets.
            </Text>
            <StyledLink to={SettingsRoutes.CustomFieldsOpenCreateModal} kind="link" size="compact">
              Create custom field
            </StyledLink>
          </EmptyStateContainer>
        )}
        {availableCustomFields.length > 0 &&
          availableCustomFields.map(customField => (
            <TicketInfoCustomField
              key={customField.id}
              displayName={customField.displayName}
              value={ticket.customFields?.[customField.apiKey as keyof typeof ticket.customFields]}
              apiKey={`key${customField.apiKey}`}
              type={customField.type}
              ticketId={ticket.id}
              ticketSilo={ticket.silo}
              roleLevel={customField.roleLevel}
              isMemberOfTicketTeam={isMemberOfTicketTeam}
              isChildTicket={Boolean(ticket.parentTicket)}
            />
          ))}
      </AccordionItem>
    </SectionWrapper>
  );
};

const EmptyStateContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding: ${withVar(SpacingToken.Spacing2)} ${withVar(SpacingToken.Spacing6)};
  gap: ${withVar(SpacingToken.Spacing2)};
`;
